import { AlertTriangle } from 'lucide-react'
import { useEquipmentScope } from './equipmentScope'
import { Switch } from '@/components/ui/switch'
import { CYCLE_ORDER, type CalendarInfo, type EquipmentSchedule } from '@/types/equipment'

/**
 * Just enough of a type to say what switching the flag off would stop.
 *
 * Structural rather than `EquipmentTypeRow` so the edit dialog — which works
 * from its own narrower `EditableEquipmentType` — can pass what it has without
 * either shape having to grow to meet the other.
 */
export type ScheduledWork = {
  schedule: EquipmentSchedule
  calendar?: CalendarInfo
}

/**
 * Whether a kind of machine is serviced on a cycle at all.
 *
 * Shared by the add and edit dialogs so the wording, and the consequence of
 * switching it off, are stated once. A separate axis from Purpose: a pharmacy
 * fridge is storage AND calibrated, ambient shelving is storage and never
 * touched, so Purpose alone cannot answer this.
 */
export function MaintainedField({
  value,
  onChange,
  /** The type being edited, when there is one — drives the warning below. */
  existing,
}: {
  value: boolean
  onChange: (value: boolean) => void
  existing?: ScheduledWork
}) {
  const scope = useEquipmentScope()
  const { copy } = scope
  const losing = existing && value === false ? whatStops(existing) : null

  return (
    <div className='space-y-2 rounded-md border p-3'>
      <div className='flex items-start justify-between gap-3'>
        <div className='space-y-0.5'>
          <label className='text-xs font-medium text-gray-600'>{copy.maintained}</label>
          <p className='text-[11px] text-muted-foreground'>{copy.maintainedHelp}</p>
        </div>
        <Switch checked={value} onCheckedChange={onChange} />
      </div>

      {value === false && (
        <p className='text-[11px] text-muted-foreground'>
          No schedules, calendar or tasks. Still listed — filter by {scope.variant === 'room' ? 'cleaning' : 'maintenance'} to find it.
        </p>
      )}

      {/* Say what is about to stop, with the count, before it is saved. Turning
          this off on a type someone has already scheduled is the one case where
          the switch destroys work rather than just hiding a control. */}
      {losing && (
        <div className='flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900'>
          <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          <span>
            {losing} will stop. Kept, not deleted — switch back on to restore.
          </span>
        </div>
      )}
    </div>
  )
}

/** "Its 3 cycles and 2 calendar events", or null when nothing is configured. */
function whatStops(row: ScheduledWork): string | null {
  const cycles = CYCLE_ORDER.filter((c) => row.schedule[c]).length
  const events = row.calendar?.events.length ?? 0
  if (cycles === 0 && events === 0) return null

  const parts: string[] = []
  if (cycles > 0) parts.push(`${cycles} configured ${cycles === 1 ? 'cycle' : 'cycles'}`)
  if (events > 0) parts.push(`${events} calendar ${events === 1 ? 'event' : 'events'}`)
  return `Its ${parts.join(' and ')}`
}
